import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheck, Trophy, Sparkles, CheckCircle2, X, Sun, Moon } from 'lucide-react';
import { settingsApi } from '@services/api';

export function ReportConfirmationPage() {
  const [searchParams] = useSearchParams();
  const c = searchParams.get('c') || '';
  const u = searchParams.get('u') || '';
  const email = searchParams.get('email') || '';
  const statusParam = searchParams.get('status') || 'success';
  const queryTheme = searchParams.get('theme') as 'light' | 'dark' | null;

  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (queryTheme === 'light' || queryTheme === 'dark') return queryTheme;
    const saved = localStorage.getItem('bcrip_report_theme');
    return saved === 'dark' ? 'dark' : 'light';
  });

  const [config, setConfig] = useState<any>(null);

  useEffect(() => {
    // Attempt to load tenant configuration if available
    settingsApi.getBatchInit().then((res) => {
      if (res.data?.success) {
        const settings = res.data.data?.settings;
        if (settings?.phishingReportConfirmation) {
          setConfig(settings.phishingReportConfirmation);
          if (!queryTheme && !localStorage.getItem('bcrip_report_theme') && settings.phishingReportConfirmation.theme) {
            setTheme(settings.phishingReportConfirmation.theme === 'dark' ? 'dark' : 'light');
          }
        }
      }
    }).catch(() => {});
  }, [queryTheme]);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('bcrip_report_theme', next);
  };

  const isAlready = statusParam === 'already_reported';
  const isSimulation = Boolean(c);

  const title = config?.title || (isAlready ? 'Email Already Reported' : '🎉 Great Catch! Phishing Reported');
  const simMsg = config?.simulationMessage || 'You successfully identified and reported a simulated phishing attack from your security team. Excellent vigilance!';
  const realMsg = config?.realEmailMessage || 'Thank you for reporting this email. Our Security Operations Center (SOC) has received it for investigation.';
  const message = isAlready
    ? 'You have already reported this email. Our Security Operations team has your response recorded.'
    : (isSimulation ? simMsg : realMsg);

  const buttonText = config?.buttonText || 'Close & Return to Mailbox';
  const companyName = config?.companyName || 'BC-RIP Security Operations';
  const showReinforcement = config ? config.showPositiveReinforcement !== false : true;
  const customHtml = config?.customHtml || '';

  const handleClose = () => {
    window.close();
    history.back();
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 font-sans transition-colors duration-300 ${
        theme === 'light'
          ? 'bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-100 via-slate-200 to-slate-300 text-slate-900'
          : 'bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-black text-white'
      }`}
    >
      <div
        className={`relative w-full max-w-lg rounded-3xl p-8 sm:p-10 text-center transition-all duration-300 animate-in fade-in zoom-in-95 ${
          theme === 'light'
            ? 'border border-slate-200/90 bg-white/95 backdrop-blur-2xl shadow-2xl shadow-slate-300/40 ring-1 ring-black/5'
            : 'border border-slate-700/80 bg-slate-900/90 backdrop-blur-2xl shadow-2xl ring-1 ring-white/10'
        }`}
      >
        {/* Top Controls: Theme Toggle & Close */}
        <div className="absolute top-5 right-5 flex items-center gap-2">
          <button
            type="button"
            onClick={toggleTheme}
            title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer ${
              theme === 'light'
                ? 'bg-slate-100 border-slate-300 text-slate-700 hover:bg-slate-200'
                : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700'
            }`}
          >
            {theme === 'light' ? (
              <>
                <Sun className="w-3.5 h-3.5 text-amber-500" />
                <span>Light</span>
              </>
            ) : (
              <>
                <Moon className="w-3.5 h-3.5 text-sky-400" />
                <span>Dark</span>
              </>
            )}
          </button>
          <button
            type="button"
            onClick={handleClose}
            className={`rounded-full p-1.5 transition-colors cursor-pointer ${
              theme === 'light'
                ? 'text-slate-400 hover:text-slate-800 hover:bg-slate-100'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-tr from-emerald-500 to-teal-400 text-white shadow-xl shadow-emerald-500/25 ring-8 ring-emerald-500/10">
          {isSimulation ? (
            <Trophy className="h-10 w-10 text-white" />
          ) : (
            <ShieldCheck className="h-10 w-10 text-white" />
          )}
        </div>

        <h1
          className={`text-2xl sm:text-3xl font-extrabold tracking-tight leading-snug ${
            theme === 'light' ? 'text-slate-900' : 'text-white'
          }`}
        >
          {title}
        </h1>

        {showReinforcement && (
          <div
            className={`my-4 inline-flex items-center gap-2 rounded-full px-4 py-1 text-xs sm:text-sm font-semibold shadow-sm border ${
              theme === 'light'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-emerald-500/15 border-emerald-500/35 text-emerald-300'
            }`}
          >
            <Sparkles className="w-4 h-4 text-emerald-500" />
            Security Champion: +50 Resilience Points Awarded
          </div>
        )}

        <p
          className={`text-sm sm:text-base leading-relaxed mt-2 px-2 ${
            theme === 'light' ? 'text-slate-600' : 'text-slate-300'
          }`}
        >
          {message}
        </p>

        {(c || email) && (
          <div
            className={`mt-6 rounded-2xl p-4 text-left text-xs space-y-2 border ${
              theme === 'light'
                ? 'border-slate-200 bg-slate-50/80 text-slate-600'
                : 'border-slate-800 bg-slate-950/60 text-slate-400'
            }`}
          >
            {c && (
              <div className="flex justify-between items-center">
                <span>Campaign:</span>
                <span className={`font-semibold ${theme === 'light' ? 'text-slate-900' : 'text-slate-200'}`}>
                  Security Awareness Simulation
                </span>
              </div>
            )}
            {email && (
              <div className="flex justify-between items-center">
                <span>Reported By:</span>
                <span className={`font-semibold ${theme === 'light' ? 'text-slate-900' : 'text-slate-200'}`}>
                  {email}
                </span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span>Status:</span>
              <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Logged in Security Dashboard
              </span>
            </div>
          </div>
        )}

        {customHtml ? (
          <div
            className={`mt-5 text-xs border-t pt-3 ${
              theme === 'light' ? 'border-slate-200 text-slate-500' : 'border-slate-800 text-slate-400'
            }`}
            dangerouslySetInnerHTML={{ __html: customHtml }}
          />
        ) : (
          <div
            className={`mt-5 text-xs border-t pt-3 ${
              theme === 'light' ? 'border-slate-200 text-slate-500' : 'border-slate-800 text-slate-400'
            }`}
          >
            Protected & verified by{' '}
            <strong className={theme === 'light' ? 'text-slate-800' : 'text-slate-200'}>
              {companyName}
            </strong>
          </div>
        )}

        <div className="mt-6">
          <button
            type="button"
            onClick={handleClose}
            className="w-full rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold py-3.5 px-6 text-sm sm:text-base shadow-lg shadow-emerald-900/30 transition-all hover:scale-[1.01] active:scale-[0.99] cursor-pointer"
          >
            {buttonText}
          </button>
        </div>
      </div>
    </div>
  );
}
